import { Link } from "react-router-dom";

// Structured, brand-aligned renderer for a content page.
// Used by both the public route (/p/:slug) and the Ops Center live preview.
export const PublicPageView = ({ page, preview = false }) => {
  if (!page) return null;

  const {
    hero_heading, hero_subheading, hero_meta, image_url,
    body, cta_label, cta_url, give_note, title,
  } = page;

  const heading = hero_heading || title || "";
  const isInternal = cta_url && cta_url.startsWith("/");

  const CtaButton = () => {
    if (!cta_label) return null;
    const cls =
      "inline-flex items-center justify-center bg-[#0A1A2F] text-white hover:bg-[#0A1A2F]/90 " +
      "border-b-2 border-transparent hover:border-[#D4AF37] transition-colors rounded-sm " +
      "px-8 py-4 text-sm font-semibold tracking-wide uppercase shadow-md focus-visible:outline-none " +
      "focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2";
    if (preview) {
      return <span className={cls} data-testid="page-cta-button">{cta_label}</span>;
    }
    if (isInternal) {
      return <Link to={cta_url} className={cls} data-testid="page-cta-button">{cta_label}</Link>;
    }
    return (
      <a href={cta_url || "#"} target="_blank" rel="noreferrer" className={cls} data-testid="page-cta-button">
        {cta_label}
      </a>
    );
  };

  return (
    <div
      className={`w-full bg-[#FDFBF7] ${preview ? "" : "min-h-screen"}`}
      data-testid="public-page-view"
    >
      <div className="grid grid-cols-1 md:grid-cols-12">
        {/* Left — flyer / hero image (uncropped) */}
        {image_url && (
          <div className="md:col-span-5 bg-[#0A1A2F]/[0.03] border-b md:border-b-0 md:border-r border-[#D4AF37]/20">
            <div className={`md:sticky md:top-0 flex items-start justify-center p-5 sm:p-8 ${preview ? "" : "md:min-h-screen"}`}>
              <img
                src={image_url}
                alt={heading}
                className="w-full h-auto max-w-md rounded-sm shadow-[0_8px_32px_rgba(10,26,47,0.15)] object-contain hover:scale-[1.02] transition-transform duration-700"
                data-testid="page-hero-image"
              />
            </div>
          </div>
        )}

        {/* Right — content */}
        <div className={`${image_url ? "md:col-span-7" : "md:col-span-12"} px-6 py-12 sm:px-10 md:px-16 md:py-20`}>
          <div className="max-w-2xl">
            {hero_subheading && (
              <p className="text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-4" data-testid="page-subheading">
                {hero_subheading}
              </p>
            )}
            {heading && (
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight leading-none text-[#0A1A2F]" data-testid="page-heading">
                {heading}
              </h1>
            )}
            {hero_meta && (
              <p className="mt-5 text-slate-500 text-base" data-testid="page-meta">{hero_meta}</p>
            )}

            {(hero_meta || heading) && <div className="mt-8 w-16 h-[3px] bg-[#D4AF37]" />}

            {body && (
              <div className="mt-8 text-lg leading-relaxed text-slate-700 whitespace-pre-wrap" data-testid="page-body">
                {body}
              </div>
            )}

            {cta_label && (
              <div className="mt-12">
                <CtaButton />
                {give_note && (
                  <p className="mt-4 text-sm text-slate-500 whitespace-pre-wrap" data-testid="page-give-note">{give_note}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicPageView;
